import { useState, type FormEvent, type ReactNode } from 'react'
import type { PublicUserDTO } from '@wf/shared'
import { ApiError } from '../api/client'
import { useAuth } from '../app/auth'
import { useSetEmail, useVerifyOwnEmail } from '../onboarding/useOnboarding'
import { QrCode } from './QrCode'
import { PinCard } from './PinCard'
import { useChangeName, useChangePassword, useChangeUsername, useTwofaDisable, useTwofaEnable, useTwofaSetup } from './useAccount'

/** Mein Konto: alle eigenen Stammdaten (Name, Benutzername, E-Mail, Passwort) strukturiert mit Stift je Zeile
 *  bearbeiten, plus Zwei-Faktor-Authentifizierung. Erreichbar über das Profil-Menü (oben rechts). Für jeden
 *  eingeloggten Nutzer — der Server erzwingt die Rechte ohnehin. */
export function AccountPage() {
  const { user } = useAuth()
  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Mein Konto</h2>
          <p className="sub">Deine Daten und Anmeldung verwalten</p>
        </div>
      </div>
      {user && (
        <div className="wf-card">
          <h3>Meine Daten</h3>
          <div className="wf-datalist">
            <NameRow me={user} />
            <UsernameRow me={user} />
            <EmailRow me={user} />
            <PasswordRow />
          </div>
        </div>
      )}
      {user && <TwofaSection enabled={user.totpEnabled} isSuperadmin={user.isSuperadmin} />}
      {/* Terminal-PIN: nur für Mandanten-Nutzer sinnvoll — der Betreiber stempelt nie an einem Werkstatt-Tablet. */}
      {user && !user.isSuperadmin && <PinCard />}
    </section>
  )
}

/** Gemeinsame Zeilen-Hülle: Label + Wert links, Stift rechts; bei Bearbeitung klappt darunter das Formular auf. */
function DataRow({
  label,
  value,
  editLabel,
  editing,
  onToggle,
  children,
}: {
  label: string
  value: ReactNode
  editLabel: string
  editing: boolean
  onToggle: () => void
  children: ReactNode
}) {
  return (
    <div className="wf-datarow">
      <div className="wf-datarow__head">
        <div className="wf-datarow__main">
          <span className="wf-datarow__label">{label}</span>
          <div className="wf-datarow__value">{value}</div>
        </div>
        <button type="button" className="wf-iconbtn wf-card__edit" aria-label={editLabel} aria-expanded={editing} onClick={onToggle}>
          ✎
        </button>
      </div>
      {editing && <div className="wf-datarow__edit">{children}</div>}
    </div>
  )
}

function NameRow({ me }: { me: PublicUserDTO }) {
  const change = useChangeName()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(me.name)
  function open() {
    setValue(me.name)
    change.reset()
    setEditing(true)
  }
  function submit(e: FormEvent) {
    e.preventDefault()
    const name = value.trim()
    if (!name) return
    change.mutate({ name }, { onSuccess: () => setEditing(false) })
  }
  return (
    <DataRow label="Name" value={me.name} editLabel="Name bearbeiten" editing={editing} onToggle={() => (editing ? setEditing(false) : open())}>
      <form className="wf-adminform" onSubmit={submit}>
        <label className="wf-field">
          <span className="wf-label">Anzeigename (nur in der App sichtbar)</span>
          <input className="wf-input" value={value} maxLength={80} onChange={(e) => setValue(e.target.value)} autoFocus />
        </label>
        {change.isError && <div className="error" role="alert">{(change.error as Error).message}</div>}
        <div className="wf-form__actions">
          <button type="submit" className="wf-btn wf-btn--primary wf-btn--sm" disabled={change.isPending || !value.trim()}>
            {change.isPending ? 'Speichere…' : 'Speichern'}
          </button>
          <button type="button" className="wf-btn wf-btn--sm" onClick={() => setEditing(false)}>
            Abbrechen
          </button>
        </div>
      </form>
    </DataRow>
  )
}

function UsernameRow({ me }: { me: PublicUserDTO }) {
  const change = useChangeUsername()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(me.username)
  function open() {
    setValue(me.username)
    change.reset()
    setEditing(true)
  }
  function submit(e: FormEvent) {
    e.preventDefault()
    const username = value.trim()
    if (!username) return
    change.mutate({ username }, { onSuccess: () => setEditing(false) })
  }
  return (
    <DataRow
      label="Benutzername"
      value={me.username}
      editLabel="Benutzername bearbeiten"
      editing={editing}
      onToggle={() => (editing ? setEditing(false) : open())}
    >
      <form className="wf-adminform" onSubmit={submit}>
        <label className="wf-field">
          <span className="wf-label">Benutzername (zum Anmelden, muss noch frei sein)</span>
          <input
            className="wf-input"
            value={value}
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            onChange={(e) => setValue(e.target.value.toLowerCase().replace(/\s/g, ''))}
            autoFocus
          />
        </label>
        {change.isError && <div className="error" role="alert">{(change.error as Error).message}</div>}
        <div className="wf-form__actions">
          <button type="submit" className="wf-btn wf-btn--primary wf-btn--sm" disabled={change.isPending || !value.trim()}>
            {change.isPending ? 'Speichere…' : 'Speichern'}
          </button>
          <button type="button" className="wf-btn wf-btn--sm" onClick={() => setEditing(false)}>
            Abbrechen
          </button>
        </div>
      </form>
    </DataRow>
  )
}

// E-Mail verwalten (aus dem früheren Team-Reiter hierher gezogen): hinzufügen/ändern → 6-stelligen Code bestätigen.
// Eine VERIFIZIERTE E-Mail schaltet den Passwort-Reset per E-Mail frei. Login bleibt per Benutzername/Passwort.
function EmailRow({ me }: { me: PublicUserDTO }) {
  const setEmail = useSetEmail()
  const verify = useVerifyOwnEmail()
  const [editing, setEditing] = useState(false)
  const [awaitingCode, setAwaitingCode] = useState(false)
  const [emailInput, setEmailInput] = useState(me.email ?? '')
  const [code, setCode] = useState('')

  function sendCode(e?: FormEvent) {
    e?.preventDefault()
    setEmail.mutate({ email: emailInput.trim().toLowerCase() }, { onSuccess: () => { setAwaitingCode(true); setCode('') } })
  }
  function confirm(e: FormEvent) {
    e.preventDefault()
    verify.mutate({ code: code.trim() }, { onSuccess: () => { setEditing(false); setAwaitingCode(false); setCode('') } })
  }
  function open() {
    setEmailInput(me.email ?? '')
    setAwaitingCode(false)
    setEmail.reset()
    verify.reset()
    setEditing(true)
  }
  function requestCodeForCurrent() {
    setEmailInput(me.email ?? '')
    setEditing(true)
    setEmail.mutate({ email: (me.email ?? '').toLowerCase() }, { onSuccess: () => { setAwaitingCode(true); setCode('') } })
  }

  const setErr = setEmail.error instanceof ApiError ? setEmail.error.message : setEmail.isError ? 'Senden fehlgeschlagen' : null
  const verErr = verify.error instanceof ApiError ? verify.error.message : verify.isError ? 'Bestätigung fehlgeschlagen' : null

  const value = me.email ? (
    <span>
      {me.email}{' '}
      {me.emailVerified ? <span className="wf-tag">verifiziert ✓</span> : <span className="wf-tag wf-tag--off">unbestätigt</span>}
      {!me.emailVerified && (
        <button type="button" className="wf-linkbtn" onClick={requestCodeForCurrent} disabled={setEmail.isPending}>
          Bestätigungscode anfordern
        </button>
      )}
    </span>
  ) : (
    <span className="muted">Keine E-Mail hinterlegt</span>
  )

  return (
    <DataRow
      label="E-Mail"
      value={value}
      editLabel={me.email ? 'E-Mail bearbeiten' : 'E-Mail hinzufügen'}
      editing={editing}
      onToggle={() => (editing ? setEditing(false) : open())}
    >
      {!awaitingCode ? (
        <form className="wf-adminform" onSubmit={sendCode}>
          <label className="wf-field">
            <span className="wf-label">E-Mail-Adresse (muss neu bestätigt werden)</span>
            <input
              className="wf-input"
              type="email"
              value={emailInput}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setEmailInput(e.target.value)}
              autoFocus
            />
          </label>
          {setErr && <div className="error" role="alert">{setErr}</div>}
          <div className="wf-form__actions">
            <button type="submit" className="wf-btn wf-btn--primary wf-btn--sm" disabled={setEmail.isPending || !emailInput.trim()}>
              {setEmail.isPending ? 'Sende Code…' : 'Code senden'}
            </button>
            <button type="button" className="wf-btn wf-btn--sm" onClick={() => setEditing(false)} disabled={setEmail.isPending}>
              Abbrechen
            </button>
          </div>
        </form>
      ) : (
        <form className="wf-adminform" onSubmit={confirm}>
          <p className="hint">Wir haben einen 6-stelligen Code an {emailInput} geschickt (15 Minuten gültig).</p>
          <label className="wf-field">
            <span className="wf-label">Bestätigungscode</span>
            <input
              className="wf-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              autoFocus
            />
          </label>
          {verErr && <div className="error" role="alert">{verErr}</div>}
          <div className="wf-form__actions">
            <button type="submit" className="wf-btn wf-btn--primary wf-btn--sm" disabled={verify.isPending || code.length !== 6}>
              {verify.isPending ? 'Prüfe…' : 'Bestätigen'}
            </button>
            <button type="button" className="wf-linkbtn" onClick={() => sendCode()} disabled={setEmail.isPending}>
              Code erneut senden
            </button>
          </div>
        </form>
      )}
    </DataRow>
  )
}

function PasswordRow() {
  const change = useChangePassword()
  const [editing, setEditing] = useState(false)
  const [oldPw, setOldPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirm, setConfirm] = useState('')
  const [done, setDone] = useState(false)
  const mismatch = confirm.length > 0 && newPw !== confirm
  const valid = oldPw.length > 0 && newPw.length >= 8 && newPw === confirm

  function reset() {
    setOldPw('')
    setNewPw('')
    setConfirm('')
    setDone(false)
    change.reset()
  }
  function submit(e: FormEvent) {
    e.preventDefault()
    if (!valid) return
    change.mutate(
      { old: oldPw, new: newPw },
      {
        onSuccess: () => {
          setOldPw('')
          setNewPw('')
          setConfirm('')
          setDone(true)
        },
      },
    )
  }
  return (
    <DataRow
      label="Passwort"
      value={<span className="wf-datarow__dots">••••••••</span>}
      editLabel="Passwort bearbeiten"
      editing={editing}
      onToggle={() => {
        if (editing) setEditing(false)
        else {
          reset()
          setEditing(true)
        }
      }}
    >
      <form className="wf-adminform" onSubmit={submit}>
        <label className="wf-field">
          <span className="wf-label">Aktuelles Passwort</span>
          <input
            className="wf-input"
            type="password"
            autoComplete="current-password"
            value={oldPw}
            onChange={(e) => {
              setOldPw(e.target.value)
              setDone(false)
            }}
            required
          />
        </label>
        <div className="two">
          <label className="wf-field">
            <span className="wf-label">Neues Passwort (min. 8)</span>
            <input
              className="wf-input"
              type="password"
              autoComplete="new-password"
              value={newPw}
              minLength={8}
              onChange={(e) => {
                setNewPw(e.target.value)
                setDone(false)
              }}
              required
            />
          </label>
          <label className="wf-field">
            <span className="wf-label">Neues Passwort wiederholen</span>
            <input className="wf-input" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </label>
        </div>
        {mismatch && <div className="error" role="alert">Die Passwörter stimmen nicht überein.</div>}
        {change.isError && (
          <div className="error" role="alert">
            {(change.error as Error).message}
          </div>
        )}
        {done && <div className="wf-adminrow__note">✓ Passwort wurde geändert.</div>}
        <div className="wf-form__actions">
          <button type="submit" className="wf-btn wf-btn--primary wf-btn--sm" disabled={change.isPending || !valid}>
            Passwort ändern
          </button>
          <button type="button" className="wf-btn wf-btn--sm" onClick={() => setEditing(false)}>
            Schließen
          </button>
        </div>
      </form>
    </DataRow>
  )
}

function TwofaSection({ enabled, isSuperadmin }: { enabled: boolean; isSuperadmin: boolean }) {
  const setup = useTwofaSetup()
  const enable = useTwofaEnable()
  const disable = useTwofaDisable()
  const [code, setCode] = useState('')
  const [disablePw, setDisablePw] = useState('')
  const [recovery, setRecovery] = useState<string[] | null>(null)

  // Frisch eingerichtet → Recovery-Codes EINMALIG anzeigen.
  if (recovery) {
    return (
      <div className="wf-card">
        <h3>Zwei-Faktor-Authentifizierung</h3>
        <p>✓ 2FA ist jetzt aktiv. <strong>Sichere diese Recovery-Codes</strong> (Notzugang, falls dein Handy weg ist):</p>
        <pre className="wf-recovery">{recovery.join('   ')}</pre>
      </div>
    )
  }

  if (enabled) {
    return (
      <div className="wf-card">
        <h3>Zwei-Faktor-Authentifizierung</h3>
        <p>
          ✓ 2FA ist <strong>aktiv</strong>.
        </p>
        {isSuperadmin ? (
          <p className="muted">Für das Betreiber-Konto verpflichtend — kann nicht deaktiviert werden.</p>
        ) : (
          <>
            <div className="wf-adminrow__pwreset">
              <input
                className="wf-input"
                type="password"
                placeholder="Passwort zum Deaktivieren"
                value={disablePw}
                onChange={(e) => setDisablePw(e.target.value)}
              />
              <button
                type="button"
                className="wf-btn wf-btn--sm wf-btn--danger"
                disabled={disable.isPending || !disablePw}
                onClick={() => disable.mutate({ password: disablePw }, { onSuccess: () => setDisablePw('') })}
              >
                2FA deaktivieren
              </button>
            </div>
            {disable.isError && (
              <div className="error" role="alert">
                {(disable.error as Error).message}
              </div>
            )}
          </>
        )}
      </div>
    )
  }

  // Nicht aktiv → Einrichten.
  return (
    <div className="wf-card">
      <h3>Zwei-Faktor-Authentifizierung</h3>
      {!setup.data ? (
        <>
          <p className="muted">Schütze dein Konto mit einem zweiten Faktor (Authenticator-App).</p>
          <div className="wf-form__actions">
            <button type="button" className="wf-btn wf-btn--primary" disabled={setup.isPending} onClick={() => setup.mutate()}>
              2FA einrichten
            </button>
          </div>
        </>
      ) : (
        <>
          <p>In der Authenticator-App ein Konto „Cevio" anlegen: QR-Code scannen — oder ohne Kamera das Secret unten manuell eintragen (6-stellig, 30 s):</p>
          <QrCode value={setup.data.otpauth} label="QR-Code für die Authenticator-App" />
          <pre className="wf-recovery">{setup.data.secret}</pre>
          <p>Dann den aktuell angezeigten 6-stelligen Code zur Bestätigung eingeben:</p>
          <div className="wf-adminrow__pwreset">
            <input className="wf-input" inputMode="numeric" placeholder="6-stelliger Code" value={code} onChange={(e) => setCode(e.target.value)} />
            <button
              type="button"
              className="wf-btn wf-btn--sm wf-btn--primary"
              disabled={enable.isPending || code.length < 6}
              onClick={() => enable.mutate({ code }, { onSuccess: (res) => { setRecovery(res.recovery); setCode('') } })}
            >
              Aktivieren
            </button>
          </div>
        </>
      )}
      {(setup.isError || enable.isError) && (
        <div className="error" role="alert">
          {((setup.error || enable.error) as Error).message}
        </div>
      )}
    </div>
  )
}
